import { Controller } from "@hotwired/stimulus"

// Shared chart controller for every Charts::* component (Bar, Line, Doughnut,
// Sparkline). One controller drives all types via the `type` value, so a
// dashboard of charts loads Chart.js exactly once (lazy, on first chart).
//
// Theme-aware: series colors are read from the design-system CSS custom
// properties (--primary-500, --success-500, …) at render time and re-read when
// the theme toggles (class-based dark mode). Neither host controller it
// replaces (roi--chart, iot--chart) does this.
//
// Currency/locale: number formatting via Intl.NumberFormat. Nordic currencies
// (SEK/NOK/DKK/ISK) and always-code currencies (AED/SAR) render as ISO code;
// suffix locales (de/es/fr/it/pl/da/sv/ar) place the symbol/code after the
// number — mirroring the host's CurrencyFormatter display policy.
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    type: String,
    data: Object,
    options: Object,
    currency: String,
    locale: String,
    palette: Array,
    intent: String,
  }

  static chartPromise = null

  connect() {
    this._chart = null
    this._themeObserver = null
    this.render()
  }

  disconnect() {
    if (this._themeObserver) this._themeObserver.disconnect()
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  async loadChart() {
    if (!this.constructor.chartPromise) {
      // "chart.js/auto" pre-registers every controller/scale/element/plugin,
      // so no manual Chart.register(...registerables). The bare "chart.js"
      // specifier is not safe here: host dev servers serve this controller
      // from the gem's @fs path (outside the vite root), where vite resolves
      // the bare specifier with the *require* condition to dist/chart.cjs —
      // and the browser throws "ReferenceError: require is not defined",
      // leaving every chart a permanently blank canvas (conek-rails browser
      // QA 2026-09-10; production builds were never affected — rollup bundles
      // the ESM entry). "chart.js/auto" is also what the host's own chart
      // controllers import, so vite pre-bundles it as ESM on both sides.
      this.constructor.chartPromise = import("chart.js/auto").then(
        (mod) => mod.Chart ?? mod.default,
      )
    }
    return this.constructor.chartPromise
  }

  async render() {
    if (!this.hasCanvasTarget) return
    const Chart = await this.loadChart()
    if (!this.element.isConnected) return // disconnected while importing

    const config = {
      type: this.typeValue,
      data: this.applyColors(this.dataValue),
      options: this.buildOptions(),
    }

    if (this._chart) {
      this._chart.data = config.data
      this._chart.options = config.options
      this._chart.update()
    } else {
      this._chart = new Chart(this.canvasTarget.getContext("2d"), config)
    }

    this.observeTheme()
  }

  // Re-read token colors when the theme class flips anywhere up the tree.
  observeTheme() {
    if (this._themeObserver) return
    const root = document.documentElement
    this._themeObserver = new MutationObserver(() => {
      if (this._chart) {
        this._chart.data = this.applyColors(this.dataValue)
        this._chart.options = this.buildOptions()
        this._chart.update()
      }
    })
    this._themeObserver.observe(root, { attributes: true, attributeFilter: ["class"] })
  }

  // -- Color -----------------------------------------------------------------

  // Token scope: tokens resolve from the chart canvas's own inherited value,
  // so a scoped `.dark` wrapper (the kit's wrapper-scoped dark mode — work
  // previews, host theme surfaces) drives the palette even when the document
  // root itself is still light. With no wrapper the inherited value IS the
  // root value, so behavior is unchanged for root-scoped hosts.
  get colorScope() {
    if (!this.hasCanvasTarget) return document.documentElement
    return this.canvasTarget.closest(".dark") || document.documentElement
  }

  cssVar(name, fallback, scope = document.documentElement) {
    const v = getComputedStyle(scope).getPropertyValue(name).trim()
    return v || fallback
  }

  // Design tokens store HSL channels (`--primary-500: 183 55% 50%`), not a
  // ready color. Wrap bare channels in hsl(); pass through anything already a
  // color (rgb/hex/var). Both space ("183 55% 50%") and comma ("205, 20%, 20%")
  // channel forms appear across the light/dark token sets, and channels carry
  // decimals ("--muted: 0 0% 96.1%") — a rejected channel string would reach
  // Chart.js unparsed and render black.
  cssColor(name, fallback, scope) {
    const raw = this.cssVar(name, null, scope || this.colorScope)
    if (!raw) return fallback
    const channels = raw.split(/\s*,\s*|\s+/).filter(Boolean)
    if (channels.length === 3 && channels.every((c) => /^\d+(?:\.\d+)?%?$/.test(c))) {
      return `hsl(${channels.join(" ")})`
    }
    return raw
  }

  // Map a palette token (primary/success/…) or an intent text class
  // (text-success) to a concrete color value from the theme.
  tokenColor(token, fallback) {
    const map = {
      primary: "--primary-500",
      success: "--success",
      warning: "--warning",
      danger: "--danger",
      info: "--info",
      muted: "--muted-foreground",
      // Gauge track: the muted background surface, lighter than muted-foreground.
      track: "--muted",
      // intent text classes → base token
      "text-success": "--success",
      "text-danger": "--danger",
      "text-warning": "--warning",
      "text-info": "--info",
      "text-foreground": "--foreground",
    }
    return this.cssColor(map[token] || `--${token}-500`, fallback || "hsl(183 55% 50%)")
  }

  // Axis chrome: ticks wear the next-muted text step (foreground-medium) —
  // muted-foreground numerals read as washed out on dark surfaces.
  axisTickColor() {
    return this.cssColor("--foreground-medium", "hsl(222 18% 38%)")
  }

  applyColors(data) {
    const palettes = this.hasPaletteValue && this.paletteValue.length
      ? this.paletteValue
      : ["primary", "success", "warning", "danger", "info", "muted"]
    const isSparkline = !!(this.optionsValue && this.optionsValue.sparkline)
    const singleIntent = this.hasIntentValue ? this.intentValue : null
    const clone = JSON.parse(JSON.stringify(data || { datasets: [] }))
    const datasets = clone.datasets || []
    // Chart.js plots a category x-axis by labels; a sparkline (points only)
    // supplies none, so synthesize index labels ONLY when there are none at
    // all — never overwrite real category labels.
    if (!Array.isArray(clone.labels) || clone.labels.length === 0) {
      const maxLen = Math.max(0, ...datasets.map((d) => (d.data || []).length))
      clone.labels = Array.from({ length: maxLen }, (_, i) => String(i))
    }
    const labels = clone.labels.length

    ;(clone.datasets || []).forEach((ds, i) => {
      if (ds.backgroundColor) return // caller-specified colors win
      // Gauge pattern: per-SEGMENT token colors (e.g. [arc, track]) on one
      // dataset — Chart.js doughnut datasets are concentric rings, so a gauge's
      // two arcs must be segments, and each needs its own theme token.
      if (ds.tokenColors) {
        ds.backgroundColor = ds.tokenColors.map((token) => this.tokenColor(token, null))
        ds.borderColor = this.cssColor("--card", "hsl(0 0% 100%)")
        return
      }
      if (singleIntent) {
        const c = this.tokenColor(singleIntent, null)
        ds.borderColor = c
        ds.backgroundColor = c
        return
      }
      if (this.typeValue === "doughnut" || this.typeValue === "pie") {
        ds.backgroundColor = Array.from({ length: labels }, (_, k) =>
          this.tokenColor(palettes[k % palettes.length], null)
        )
        ds.borderColor = this.cssColor("--card", "hsl(0 0% 100%)")
        ds.borderWidth = 2
      } else {
        const c = this.tokenColor(ds.color || palettes[i % palettes.length], null)
        if (this.typeValue === "line") {
          ds.borderColor = c
          ds.backgroundColor = isSparkline ? c : this.withAlpha(c, 0.15)
        } else {
          ds.backgroundColor = this.withAlpha(c, 0.85)
          ds.borderColor = c
          ds.borderWidth = 1
        }
      }
    })
    return clone
  }

  withAlpha(color, alpha) {
    // hsl(H S% L%) → hsla; rgb()/rgba() → rgba; hex → rgba. Fallback: raw.
    const hsl = color.match(/hsl\(([^)]+)\)/)
    if (hsl) return `hsl(${hsl[1]} / ${alpha})`
    const rgb = color.match(/rgba?\(([^)]+)\)/)
    if (rgb) {
      const parts = rgb[1].split(",").map((s) => s.trim())
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`
    }
    const hex = color.replace("#", "")
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }
    return color
  }

  // -- Options ---------------------------------------------------------------

  buildOptions() {
    const base = this.optionsValue || {}
    const themed = JSON.parse(JSON.stringify(base))
    const axisColor = this.axisTickColor()
    const gridColor = this.withAlpha(this.cssColor("--border", "hsl(0 0% 90%)"), 0.6)

    // Sparklines sit in a flex row with text; without an explicit height
    // constraint the canvas collapses. Anchor the container height.
    if (themed.sparkline) {
      this.element.style.height = this.element.style.height || "40px"
    }

    if (themed.scales) {
      const valueAxisKey = themed.indexAxis === "y" ? "x" : "y"
      Object.entries(themed.scales).forEach(([key, scale]) => {
        if (scale.display === false) return
        scale.ticks = Object.assign({ color: axisColor }, scale.ticks)
        scale.grid = Object.assign({ color: gridColor }, scale.grid)
        // Number formatting on the VALUE axis only (never the category axis,
        // which holds labels like "20%" / "Jan" — not numbers). Locale-aware
        // separators apply even with NO currency: a chart with a null
        // currency_code (mixed-currency tenant — unset by design, CONEK-4332)
        // would otherwise fall back to Chart.js default tick labels, which
        // have no grouping separators at all (conek-rails#5758 follow-up).
        if (key === valueAxisKey) {
          scale.ticks.callback = (value) => this.formatNumber(value)
        }
      })
    }
    if (this.currencyValue && themed.plugins && themed.plugins.tooltip) {
      themed.plugins.tooltip.callbacks = Object.assign(
        {},
        themed.plugins.tooltip.callbacks,
        { label: (ctx) => this.tooltipLabel(ctx) }
      )
    }
    return themed
  }

  tooltipLabel(ctx) {
    const label = ctx.dataset && ctx.dataset.label ? `${ctx.dataset.label}: ` : ""
    // The value axis flips with the chart orientation: vertical bars/lines read
    // parsed.y; horizontal bars (indexAxis "y") read parsed.x. The other axis is
    // the category index and must never be formatted as a value.
    const horizontal = this.optionsValue && this.optionsValue.indexAxis === "y"
    const value = ctx.parsed && typeof ctx.parsed === "object"
      ? (horizontal ? ctx.parsed.x : ctx.parsed.y)
      : ctx.parsed
    return `${label}${this.formatNumber(value)}`
  }

  // -- Number formatting (mirrors CurrencyFormatter display policy) ----------

  formatNumber(value) {
    if (value === null || value === undefined || isNaN(value)) return "—"
    const currency = this.currencyValue
    if (!currency) {
      return new Intl.NumberFormat(this.localeValue || "en-GB").format(value)
    }
    const NORDIC = ["SEK", "NOK", "DKK", "ISK"]
    const ALWAYS_CODE = ["AED", "SAR"]
    const SUFFIX_LOCALES = ["de", "es", "fr", "it", "pl", "da", "sv", "ar"]
    const locale = this.localeValue || "en-GB"
    const lang = locale.split("-")[0]
    const asCode = NORDIC.includes(currency) || ALWAYS_CODE.includes(currency)
    const number = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
    if (asCode) {
      return SUFFIX_LOCALES.includes(lang) ? `${number} ${currency}` : `${currency} ${number}`
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    } catch (e) {
      return `${number} ${currency}`
    }
  }

  // -- Download ---------------------------------------------------------------

  download(event) {
    if (event) event.preventDefault()
    if (!this._chart) return
    const link = document.createElement("a")
    link.download = `${(this.element.querySelector("h3")?.textContent || "chart").trim()}.png`
    link.href = this._chart.toBase64Image()
    link.click()
  }
}
